import React from 'react';
import { CheckCircle, AlertCircle, CheckSquare, Trash2, UserX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FlagManagement = ({ flaggedContent, loading, resolving, onResolveFlag }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading flagged content...</p>
      </div>
    );
  }

  if (flaggedContent.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <p className="text-xl text-gray-700 mb-2">All clear!</p>
        <p className="text-gray-500">No flagged content pending review.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-red-700 bg-red-50 p-4 rounded-lg border border-red-200">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium">
          {flaggedContent.length} {flaggedContent.length === 1 ? 'item' : 'items'} flagged for review
        </span>
      </div>

      {flaggedContent.map((flag) => (
        <div
          key={flag.id}
          className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500"
        >
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  flag.content_type === 'post' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {flag.content_type.toUpperCase()}
                </span>
                <span className="text-xs px-3 py-1 rounded-full font-medium bg-red-100 text-red-700">
                  {flag.reason.replace('_', ' ').toUpperCase()}
                </span>
                {flag.flag_count > 1 && (
                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-orange-100 text-orange-700">
                    {flag.flag_count} REPORTS
                  </span>
                )}
              </div>
              <span className="text-sm text-gray-500">
                {formatDistanceToNow(new Date(flag.created_at), { addSuffix: true })}
              </span>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-3">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Flagged Content:</span>
              </p>
              <p className="text-gray-900">{flag.content_preview}</p>
              {flag.content_author && (
                <p className="text-sm text-gray-500 mt-2">
                  By <span className="font-medium">{flag.content_author}</span>
                </p>
              )}
              
              {/* Display images if flagged content is a post with images */}
              {flag.content_type === 'post' && flag.content_images && flag.content_images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {flag.content_images.map((imageUrl) => (
                    <img
                      key={imageUrl}
                      src={`${BACKEND_URL}${imageUrl}`}
                      alt={`Flagged post image ${imageUrl.split('/').pop()}`}
                      className="rounded-lg w-full h-48 object-cover border border-gray-200"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-semibold">Report Details:</span>
              </p>
              <p className="text-sm text-gray-700 mb-2">
                Reason: <span className="font-medium">{flag.reason.replace('_', ' ')}</span>
              </p>
              {flag.comment && (
                <p className="text-sm text-gray-700">
                  Comment: "{flag.comment}"
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Reported by: {flag.user_screenname}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => onResolveFlag(flag.id, 'approve', flag.content_type)}
              disabled={resolving[flag.id]}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              <CheckSquare className="w-4 h-4" />
              <span>Dismiss (No Action)</span>
            </button>
            
            <button
              onClick={() => {
                if (window.confirm(`Delete this ${flag.content_type}? This action cannot be undone.`)) {
                  onResolveFlag(flag.id, 'delete', flag.content_type);
                }
              }}
              disabled={resolving[flag.id]}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete Content</span>
            </button>
            
            <button
              onClick={() => onResolveFlag(flag.id, 'warn', flag.content_type)}
              disabled={resolving[flag.id]}
              className="flex items-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition disabled:opacity-50"
            >
              <AlertCircle className="w-4 h-4" />
              <span>Warn User</span>
            </button>
            
            <button
              onClick={() => {
                if (window.confirm(`Ban user "${flag.content_author}"? This is a serious action.`)) {
                  onResolveFlag(flag.id, 'ban', flag.content_type);
                }
              }}
              disabled={resolving[flag.id]}
              className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition disabled:opacity-50"
            >
              <UserX className="w-4 h-4" />
              <span>Ban User</span>
            </button>

            {resolving[flag.id] && (
              <div className="flex items-center text-gray-600">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#FF8C00] border-t-transparent mr-2"></div>
                <span className="text-sm">Processing...</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FlagManagement;
